if (!customElements.get('product-info-drawer')) {
  class ProductInfoDrawer extends HTMLElement {
    constructor() {
      super();

      this.apiUrl = this.dataset.storefrontApiUrl;
      this.apiToken = this.dataset.storefrontApiToken;
      this.metaobjectType = this.dataset.metaobjectType;

      this.panel = this.querySelector('.product-info-drawer__panel');
      this.titleElement = this.querySelector(
        '[data-product-info-drawer-title]'
      );
      this.bodyElement = this.querySelector(
        '[data-product-info-drawer-body]'
      );
      this.contentElement = this.querySelector(
        '[data-product-info-drawer-content]'
      );
      this.loadingElement = this.querySelector(
        '[data-product-info-drawer-loading]'
      );
      this.errorElement = this.querySelector(
        '[data-product-info-drawer-error]'
      );

      this.cache = new Map();
      this.pendingRequests = new Map();
      this.lastTrigger = null;
      this.activeHandle = null;
      this.abortController = null;

      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onDocumentKeydown = this.onDocumentKeydown.bind(this);

      this.onPointerIntent = this.onPointerIntent.bind(this);
      this.onFocusIntent = this.onFocusIntent.bind(this);
    }

    connectedCallback() {
      document.addEventListener(
  'click',
  this.onDocumentClick,
  true
);
      document.addEventListener('keydown', this.onDocumentKeydown);
      document.addEventListener('mouseover', this.onPointerIntent);
      document.addEventListener('focusin', this.onFocusIntent);
      document.addEventListener(
        'touchstart',
        this.onPointerIntent,
        { passive: true }
      );

      
    }

    disconnectedCallback() {
      document.removeEventListener(
  'click',
  this.onDocumentClick,
  true
);
      document.removeEventListener('keydown', this.onDocumentKeydown);
      document.removeEventListener('mouseover', this.onPointerIntent);
      document.removeEventListener('focusin', this.onFocusIntent);
      document.removeEventListener(
        'touchstart',
        this.onPointerIntent
      );

      if (this.abortController) {
        this.abortController.abort();
      }
    }

    getTriggerFromEvent(event) {
        return event.target.closest(
            '[data-product-info], a[href^="#drawer:"]'
        );
        }

        getHandleFromTrigger(trigger) {
        if (!trigger) return '';

        const dataHandle =
            trigger.dataset.productInfo?.trim();

        if (dataHandle) {
            return dataHandle;
        }

        const href = trigger.getAttribute('href') || '';

        if (!href.startsWith('#drawer:')) {
            return '';
        }

        return decodeURIComponent(
            href.slice('#drawer:'.length)
        ).trim();
        }

    isNestedInteractiveElement(event, trigger) {
      const interactiveElement = event.target.closest(
        'a, button, input, select, textarea, summary, ' +
        '[role="button"], [role="link"]'
      );

      return Boolean(
        interactiveElement &&
        interactiveElement !== trigger
      );
    }

    onDocumentClick(event) {
  const closeTrigger = event.target.closest(
    '[data-product-info-drawer-close]'
  );

  if (
    closeTrigger &&
    this.contains(closeTrigger)
  ) {
    event.preventDefault();
    event.stopPropagation();
    this.close();
    return;
  }

  const trigger = this.getTriggerFromEvent(event);

  if (!trigger) return;

  if (this.isNestedInteractiveElement(event, trigger)) {
    return;
  }

  const handle = this.getHandleFromTrigger(trigger);

  if (!handle) return;

  event.preventDefault();
  this.open(handle, trigger);
}

    onDocumentKeydown(event) {
      const trigger = this.getTriggerFromEvent(event);

      if (
        trigger &&
        event.target === trigger &&
        (event.key === 'Enter' || event.key === ' ')
      ) {
        const handle = this.getHandleFromTrigger(trigger);

        if (!handle) return;

        event.preventDefault();
        this.open(handle, trigger);
        return;
      }

      if (
        this.classList.contains('active') &&
        event.key === 'Escape'
      ) {
        event.preventDefault();
        this.close();
      }
    }

    onPointerIntent(event) {
      const trigger = this.getTriggerFromEvent(event);

      if (!trigger) return;

      const handle = this.getHandleFromTrigger(trigger);

      if (!handle) return;

      this.prefetch(handle);
    }

    onFocusIntent(event) {
      const trigger = this.getTriggerFromEvent(event);

      if (!trigger) return;

      const handle = this.getHandleFromTrigger(trigger);

      if (!handle) return;

      this.prefetch(handle);
    }

    prefetch(handle) {
      if (
        this.cache.has(handle) ||
        this.pendingRequests.has(handle)
      ) {
        return;
      }

      this.getDrawer(handle).catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            `Could not prefetch drawer "${handle}":`,
            error
          );
        }
      });
    }

    async open(handle, trigger) {
      this.lastTrigger = trigger;
      this.activeHandle = handle;

      this.showDrawer();
      this.showLoading();

      try {
        const drawer = await this.getDrawer(handle);

        if (this.activeHandle !== handle) return;

        if (!drawer.enabled) {
          throw new Error('This drawer is disabled.');
        }

        this.renderDrawer(drawer);
      } catch (error) {
        if (error?.name === 'AbortError') return;
        if (this.activeHandle !== handle) return;

        console.error(
          'Product information drawer error:',
          error
        );

        this.showError(error);
      }
    }

    showDrawer() {
      this.classList.add('active');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('product-info-drawer-open');

      requestAnimationFrame(() => {
        if (
          typeof trapFocus === 'function' &&
          this.panel
        ) {
          trapFocus(this.panel);
        } else if (this.panel) {
          this.panel.focus();
        }
      });
    }

    close() {
      this.activeHandle = null;
      this.pauseMedia();

      this.classList.remove('active');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('product-info-drawer-open');

      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(this.lastTrigger);
      } else if (this.lastTrigger) {
        this.lastTrigger.focus();
      }
    }

    pauseMedia() {
      this.querySelectorAll('video').forEach((video) => {
        video.pause();
      });

      this.querySelectorAll('iframe').forEach((iframe) => {
        const src = iframe.getAttribute('src');

        if (!src) return;

        iframe.setAttribute('src', src);
      });
    }

    showLoading() {
      this.titleElement.textContent = 'Product information';
      this.contentElement.replaceChildren();
      this.errorElement.hidden = true;
      this.loadingElement.hidden = false;
      this.bodyElement.scrollTop = 0;
      this.setAttribute('aria-busy', 'true');
    }

    showError(error) {
      this.loadingElement.hidden = true;
      this.contentElement.replaceChildren();

      this.errorElement.textContent =
        error?.message ||
        'This information could not be loaded. Please try again.';

      this.errorElement.hidden = false;
      this.removeAttribute('aria-busy');
    }

    async getDrawer(handle) {
      if (this.cache.has(handle)) {
        return this.cache.get(handle);
      }

      if (this.pendingRequests.has(handle)) {
        return this.pendingRequests.get(handle);
      }

      if (!this.apiUrl || !this.apiToken || !this.metaobjectType) {
        throw new Error(
          'Storefront API configuration is incomplete.'
        );
      }

      const request = this.fetchDrawer(handle)
        .then((drawer) => {
          this.cache.set(handle, drawer);
          return drawer;
        })
        .finally(() => {
          this.pendingRequests.delete(handle);
        });

      this.pendingRequests.set(handle, request);

      return request;
    }

    async fetchDrawer(handle) {
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.apiToken
        },
        body: JSON.stringify({
          query: ProductInfoDrawer.query,
          variables: {
            handle: {
              type: this.metaobjectType,
              handle
            }
          }
        })
      });

      if (!response.ok) {
        throw new Error(
          `Storefront API request failed with ${response.status}.`
        );
      }

      const payload = await response.json();

      if (payload.errors?.length) {
        throw new Error(
          payload.errors
            .map((error) => error.message)
            .join(' ')
        );
      }

      if (!payload.data?.metaobject) {
        throw new Error(`Drawer "${handle}" was not found.`);
      }

      return this.normalizeDrawer(payload.data.metaobject);
    }

    normalizeDrawer(metaobject) {
  const nodes =
    metaobject.contentBlocks?.references?.nodes || [];

  return {
    handle: metaobject.handle,
    title:
      metaobject.displayTitle?.value ||
      metaobject.title?.value ||
      'Product information',
    enabled: this.parseBoolean(
      metaobject.enabled?.value
    ),
    blocks: nodes
      .map((node) => this.normalizeBlock(node))
      .filter(Boolean)
  };
}

    normalizeBlock(metaobject) {
      if (!metaobject) return null;

      return {
        handle: metaobject.handle,
        type: this.normalizeChoice(
          metaobject.blockType?.value
        ),
        heading: metaobject.heading?.value || '',
text: metaobject.text?.value || '',
table: metaobject.table?.value || '',
caption: metaobject.caption?.value || '',
externalVideoUrl:
          metaobject.externalVideoUrl?.value || '',
        image: metaobject.image?.reference?.image || null,
        video: metaobject.video?.reference || null
      };
    }

    normalizeChoice(value) {
      if (!value) return '';

      try {
        const parsed = JSON.parse(value);

        if (Array.isArray(parsed)) {
          return String(parsed[0] || '')
            .trim()
            .toLowerCase();
        }

        return String(parsed || '')
          .trim()
          .toLowerCase();
      } catch (error) {
        return String(value)
          .trim()
          .toLowerCase();
      }
    }

    parseBoolean(value) {
      return (
        value === true ||
        value === 'true' ||
        value === '1'
      );
    }

    renderDrawer(drawer) {
      this.titleElement.textContent = drawer.title;
      this.loadingElement.hidden = true;
      this.errorElement.hidden = true;
      this.contentElement.replaceChildren();

      const fragment = document.createDocumentFragment();

      drawer.blocks.forEach((block, index) => {
        const renderedBlock = this.renderBlock(block);

        if (!renderedBlock) return;

        renderedBlock.dataset.blockIndex = String(index);
        renderedBlock.dataset.blockType = block.type;

        fragment.appendChild(renderedBlock);
      });

      this.contentElement.appendChild(fragment);
      this.applyBlockRelationships();

      this.bodyElement.scrollTop = 0;
      this.removeAttribute('aria-busy');
    }

    applyBlockRelationships() {
      const blocks = Array.from(
        this.contentElement.children
      );

      blocks.forEach((block, index) => {
        const previousBlock = blocks[index - 1];
        const nextBlock = blocks[index + 1];

        if (previousBlock) {
          block.dataset.previousBlockType =
            previousBlock.dataset.blockType || '';
        }

        if (nextBlock) {
          block.dataset.nextBlockType =
            nextBlock.dataset.blockType || '';
        }

        if (
          block.dataset.blockType === 'heading' &&
          nextBlock
        ) {
          block.classList.add(
            'product-info-drawer__block--groups-next'
          );
        }

        if (
          block.dataset.blockType === 'image' ||
          block.dataset.blockType === 'video' ||
          block.dataset.blockType === 'external_video' ||
          block.dataset.blockType === 'external video'
        ) {
          block.classList.add(
            'product-info-drawer__block--media'
          );
        }
      });
    }

    renderBlock(block) {
      switch (block.type) {
        case 'heading':
          return this.renderHeading(block);

        case 'text':
          return this.renderText(block);

        case 'table':
          return this.renderTable(block);

        case 'image':
          return this.renderImage(block);

        case 'video':
          return this.renderVideo(block);

        case 'external_video':
        case 'external video':
          return this.renderExternalVideo(block);

        default:
          console.warn(
            `Unsupported drawer block type: "${block.type}"`
          );
          return null;
      }
    }

    createBlockElement(type) {
      const element = document.createElement('section');

      element.className =
        `product-info-drawer__block ` +
        `product-info-drawer__block--${type.replaceAll(' ', '-')}`;

      return element;
    }

    renderHeading(block) {
      if (!block.heading) return null;

      const wrapper = this.createBlockElement('heading');
      const heading = document.createElement('h3');

      heading.className =
        'product-info-drawer__heading h3';
      heading.textContent = block.heading;

      wrapper.appendChild(heading);

      return wrapper;
    }

    renderText(block) {
  if (!block.text) return null;

  const wrapper = this.createBlockElement('text');
  const text = document.createElement('div');

  text.className = 'product-info-drawer__text rte';

  const richText = this.parseRichText(block.text);

  if (richText) {
    text.appendChild(richText);
  } else {
    block.text
      .split(/\n{2,}/)
      .map((paragraph) => paragraph.trim())
      .filter(Boolean)
      .forEach((paragraphText) => {
        const paragraph = document.createElement('p');

        paragraph.textContent = paragraphText;
        text.appendChild(paragraph);
      });
  }

  wrapper.appendChild(text);

  return wrapper;
}

renderTable(block) {
  if (!block.table) return null;

  const wrapper = this.createBlockElement('table');
  const container = document.createElement('div');

  container.className =
    'product-info-drawer__table';

  container.innerHTML = block.table;

  wrapper.appendChild(container);

  return wrapper;
}



    renderImage(block) {
      if (!block.image?.url) return null;

      const wrapper = this.createBlockElement('image');
      const figure = document.createElement('figure');
      const image = document.createElement('img');

      figure.className =
        'product-info-drawer__figure';

      image.className =
        'product-info-drawer__image';

      image.src = block.image.url;
      image.alt = block.image.altText || '';
      image.loading = 'lazy';
      image.decoding = 'async';

      if (block.image.width) {
        image.width = block.image.width;
      }

      if (block.image.height) {
        image.height = block.image.height;
      }

      figure.appendChild(image);
      this.appendCaption(figure, block.caption);
      wrapper.appendChild(figure);

      return wrapper;
    }

    renderVideo(block) {
      const sources = block.video?.sources || [];

      if (!sources.length) return null;

      const wrapper = this.createBlockElement('video');
      const figure = document.createElement('figure');
      const video = document.createElement('video');

      figure.className =
        'product-info-drawer__figure';

      video.className =
        'product-info-drawer__video';

      video.controls = true;
      video.playsInline = true;
      video.preload = 'metadata';

      if (block.video.previewImage?.url) {
        video.poster =
          block.video.previewImage.url;
      }

      sources
        .slice()
        .sort((a, b) => {
          const aWidth = Number(a.width) || 0;
          const bWidth = Number(b.width) || 0;

          return bWidth - aWidth;
        })
        .forEach((sourceData) => {
          if (!sourceData.url) return;

          const source =
            document.createElement('source');

          source.src = sourceData.url;

          if (sourceData.mimeType) {
            source.type = sourceData.mimeType;
          }

          video.appendChild(source);
        });

      figure.appendChild(video);
      this.appendCaption(figure, block.caption);
      wrapper.appendChild(figure);

      return wrapper;
    }

    renderExternalVideo(block) {
      const videoData =
        this.getExternalVideoData(
          block.externalVideoUrl
        );

      if (!videoData) return null;

      const wrapper =
        this.createBlockElement('external-video');

      const figure = document.createElement('figure');
      const videoWrapper =
        document.createElement('div');

      const iframe =
        document.createElement('iframe');

      figure.className =
        'product-info-drawer__figure';

      videoWrapper.className =
        'product-info-drawer__external-video';

      iframe.src = videoData.embedUrl;
      iframe.title =
        block.caption || videoData.title;
      iframe.loading = 'lazy';
      iframe.referrerPolicy =
        'strict-origin-when-cross-origin';
      iframe.allow =
        'accelerometer; autoplay; clipboard-write; ' +
        'encrypted-media; gyroscope; picture-in-picture; web-share';
      iframe.allowFullscreen = true;

      videoWrapper.appendChild(iframe);
      figure.appendChild(videoWrapper);
      this.appendCaption(figure, block.caption);
      wrapper.appendChild(figure);

      return wrapper;
    }

    appendCaption(parent, captionText) {
      if (!captionText) return;

      const caption =
        document.createElement('figcaption');

      caption.className =
        'product-info-drawer__caption';

      caption.textContent = captionText;
      parent.appendChild(caption);
    }

    getExternalVideoData(value) {
      if (!value) return null;

      let url;

      try {
        url = new URL(
          value,
          window.location.origin
        );
      } catch (error) {
        return null;
      }

      const hostname = url.hostname
        .replace(/^www\./, '')
        .toLowerCase();

      if (hostname === 'youtu.be') {
        const videoId = url.pathname
          .split('/')
          .filter(Boolean)[0];

        if (!videoId) return null;

        return {
          title: 'YouTube video',
          embedUrl:
            `https://www.youtube-nocookie.com/embed/` +
            `${encodeURIComponent(videoId)}`
        };
      }

      if (
        hostname === 'youtube.com' ||
        hostname === 'm.youtube.com' ||
        hostname === 'youtube-nocookie.com'
      ) {
        let videoId = '';

        if (url.pathname.startsWith('/embed/')) {
          videoId = url.pathname
            .split('/')
            .filter(Boolean)[1] || '';
        } else if (url.pathname.startsWith('/shorts/')) {
          videoId = url.pathname
            .split('/')
            .filter(Boolean)[1] || '';
        } else {
          videoId = url.searchParams.get('v') || '';
        }

        if (!videoId) return null;

        return {
          title: 'YouTube video',
          embedUrl:
            `https://www.youtube-nocookie.com/embed/` +
            `${encodeURIComponent(videoId)}`
        };
      }

      if (
        hostname === 'vimeo.com' ||
        hostname === 'player.vimeo.com'
      ) {
        const videoId = url.pathname
          .split('/')
          .filter(Boolean)
          .find((part) => /^\d+$/.test(part));

        if (!videoId) return null;

        return {
          title: 'Vimeo video',
          embedUrl:
            `https://player.vimeo.com/video/${videoId}`
        };
      }

      return null;
    }

    parseRichText(value) {
      let documentValue;

      try {
        documentValue = JSON.parse(value);
      } catch (error) {
        return null;
      }

      if (
        !documentValue ||
        typeof documentValue !== 'object'
      ) {
        return null;
      }

      const fragment =
        document.createDocumentFragment();

      const children =
        documentValue.children || [];

      children.forEach((child) => {
        const node =
          this.renderRichTextNode(child);

        if (node) {
          fragment.appendChild(node);
        }
      });

      return fragment;
    }

    renderRichTextNode(node) {
      if (!node || typeof node !== 'object') {
        return null;
      }

      if (node.type === 'text') {
  let renderedNode =
    document.createTextNode(node.value || '');

  const markTypes = new Set();

  if (node.bold === true) {
    markTypes.add('bold');
  }

  if (node.italic === true) {
    markTypes.add('italic');
  }

  if (node.underline === true) {
    markTypes.add('underline');
  }

  if (node.code === true) {
    markTypes.add('code');
  }

  if (node.strikethrough === true) {
    markTypes.add('strikethrough');
  }

  const marks = Array.isArray(node.marks)
    ? node.marks
    : [];

  marks.forEach((mark) => {
    const markType =
      typeof mark === 'string'
        ? mark
        : mark?.type;

    if (markType) {
      markTypes.add(markType);
    }
  });

  const elementMap = {
    bold: 'strong',
    italic: 'em',
    underline: 'u',
    code: 'code',
    strikethrough: 's'
  };

  markTypes.forEach((markType) => {
    const tagName = elementMap[markType];

    if (!tagName) return;

    const element =
      document.createElement(tagName);

    element.appendChild(renderedNode);
    renderedNode = element;
  });

  return renderedNode;
}

      const elementMap = {
        paragraph: 'p',
        heading: this.getHeadingTag(node.level),
        bulleted_list: 'ul',
        ordered_list: 'ol',
        list_item: 'li',
        blockquote: 'blockquote'
      };

      if (node.type === 'link') {
  const rawUrl = String(node.url || '').trim();
  const isDrawerTrigger =
    rawUrl.startsWith('#drawer:');

  const href = isDrawerTrigger
    ? rawUrl
    : this.sanitizeLink(rawUrl);

  if (!href) return null;

  const anchor = document.createElement('a');

  anchor.href = href;

  if (node.target === '_blank') {
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
  }

  this.appendRichTextChildren(
    anchor,
    node.children
  );

  if (isDrawerTrigger) {
    anchor.dataset.productInfoInlineTrigger = 'true';
  }

  return anchor;
}

      const tagName = elementMap[node.type];

      if (!tagName) {
        const fragment =
          document.createDocumentFragment();

        this.appendRichTextChildren(
          fragment,
          node.children
        );

        return fragment;
      }

      const element =
        document.createElement(tagName);

      this.appendRichTextChildren(
        element,
        node.children
      );

      return element;
    }

    appendRichTextChildren(parent, children) {
  if (!Array.isArray(children)) return;

  children.forEach((child) => {
    const renderedChild =
      this.renderRichTextNode(child);

    if (!renderedChild) return;

    const isDrawerTrigger =
      renderedChild.nodeType === Node.ELEMENT_NODE &&
      renderedChild.dataset.productInfoInlineTrigger === 'true';

    if (!isDrawerTrigger) {
      parent.appendChild(renderedChild);
      return;
    }

    const previousNode = parent.lastChild;
    const wrapper = document.createElement('span');

    wrapper.className =
      'product-info-drawer__inline-trigger-group';

    /*
     * Move the final non-whitespace word from the preceding
     * text node into the wrapper with the drawer icon.
     *
     * Example:
     * "AERO KIT (bottles)" becomes:
     * "AERO KIT " + <span>(bottles)<a>i</a></span>
     */
    if (
      previousNode &&
      previousNode.nodeType === Node.TEXT_NODE
    ) {
      const previousText =
        previousNode.nodeValue || '';

      const match = previousText.match(
        /(\S+)\s*$/
      );

      if (match) {
        previousNode.nodeValue =
          previousText.slice(
            0,
            match.index
          );

        wrapper.appendChild(
          document.createTextNode(match[1])
        );
      }
    }

    wrapper.appendChild(renderedChild);
    parent.appendChild(wrapper);
  });
}

    getHeadingTag(level) {
      const normalizedLevel = Number(level);

      if (
        normalizedLevel >= 2 &&
        normalizedLevel <= 6
      ) {
        return `h${normalizedLevel}`;
      }

      return 'h3';
    }

    sanitizeLink(value) {
      if (!value) return '';

      try {
        const url = new URL(
          value,
          window.location.origin
        );

        if (
          url.protocol === 'http:' ||
          url.protocol === 'https:' ||
          url.protocol === 'mailto:' ||
          url.protocol === 'tel:'
        ) {
          return url.href;
        }
      } catch (error) {
        return '';
      }

      return '';
    }
  }

  ProductInfoDrawer.query = `
    query ProductInformationDrawer(
      $handle: MetaobjectHandleInput!
    ) {
      metaobject(handle: $handle) {
        handle

        title: field(key: "title") {
          value
        }

        displayTitle: field(key: "display_title") {
          value
        }

        enabled: field(key: "enabled") {
          value
        }

        contentBlocks: field(key: "content_blocks") {
          references(first: 50) {
            nodes {
              ... on Metaobject {
                handle

                blockType: field(key: "type") {
                  value
                }

                heading: field(key: "heading") {
                  value
                }

                text: field(key: "text") {
                  value
                }

                table: field(key: "table") {
  value
}

                caption: field(key: "caption") {
                  value
                }

                externalVideoUrl: field(
                  key: "external_video_url"
                ) {
                  value
                }

                image: field(key: "image") {
                  reference {
                    ... on MediaImage {
                      image {
                        url
                        altText
                        width
                        height
                      }
                    }
                  }
                }

                video: field(key: "video") {
                  reference {
                    ... on Video {
                      previewImage {
                        url
                        altText
                        width
                        height
                      }

                      sources {
                        url
                        mimeType
                        width
                        height
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `;

  customElements.define(
    'product-info-drawer',
    ProductInfoDrawer
  );
}